import { useCallback, useEffect, useRef, useState } from 'react';

import type { Editor } from '@tiptap/react';

import type { BrowserSpeechRecognition, SpeechRecognitionEventLike } from './speechRecognition';
import { getSpeechRecognitionConstructor } from './speechRecognition';

const appendDictationTranscript = (editor: Editor, transcript: string) => {
  const trimmedTranscript = transcript.trim();

  if (!trimmedTranscript) {
    return;
  }

  const { from } = editor.state.selection;
  const charBeforeSelection = from > 0 ? editor.state.doc.textBetween(from - 1, from, ' ', ' ') : '';
  const needsLeadingSpace = Boolean(charBeforeSelection && !/\s/.test(charBeforeSelection));
  const contentToInsert = `${needsLeadingSpace ? ' ' : ''}${trimmedTranscript} `;

  editor
    .chain()
    .focus()
    .insertContent({
      type: 'text',
      text: contentToInsert,
    })
    .run();
};

export const useMentionEditorDictation = (editor: Editor | null) => {
  const [isDictationSupported, setIsDictationSupported] = useState(false);
  const [isDictating, setIsDictating] = useState(false);
  const [isStoppingDictation, setIsStoppingDictation] = useState(false);
  const recognitionRef = useRef<BrowserSpeechRecognition | null>(null);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes browser or analytics APIs after render.
  useEffect(() => {
    const SpeechRecognitionCtor = getSpeechRecognitionConstructor();

    setIsDictationSupported(Boolean(SpeechRecognitionCtor && editor));

    if (!SpeechRecognitionCtor || !editor) {
      recognitionRef.current = null;
      setIsDictating(false);
      setIsStoppingDictation(false);
      return undefined;
    }

    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = window.navigator.language || 'en-US';
    recognition.onresult = (event: SpeechRecognitionEventLike) => {
      let finalTranscript = '';

      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];

        if (result?.isFinal) {
          finalTranscript += result[0]?.transcript ?? '';
        }
      }

      if (finalTranscript) {
        appendDictationTranscript(editor, finalTranscript);
      }
    };
    recognition.onerror = () => {
      setIsDictating(false);
      setIsStoppingDictation(false);
    };
    recognition.onend = () => {
      setIsDictating(false);
      setIsStoppingDictation(false);
    };
    recognitionRef.current = recognition;

    return () => {
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      recognition.abort();
      recognitionRef.current = null;
    };
  }, [editor]);

  const toggleDictation = useCallback(() => {
    const recognition = recognitionRef.current;

    if (!recognition || isStoppingDictation) {
      return;
    }

    if (isDictating) {
      setIsStoppingDictation(true);

      try {
        recognition.stop();
      } catch {
        setIsStoppingDictation(false);
      }
      return;
    }

    try {
      recognition.start();
      setIsDictating(true);
      setIsStoppingDictation(false);
    } catch {
      setIsDictating(false);
      setIsStoppingDictation(false);
    }
  }, [isDictating, isStoppingDictation]);

  return {
    isDictationSupported,
    isDictating,
    isStoppingDictation,
    toggleDictation,
  };
};
